const USER_WEB_APP_RESULT_FEATURE_DEFAULTS = Object.freeze({
  rr: false,
  hr: false,
  hrv: false,
  tv: false,
  fev1: false,
  fvc: false,
  pef: false,
  fev1Fvc: false,
  flowLoops: false,
  relativeChange: false,
  resultsTrends: false,
  cardiacQuality: false,
  signalQuality: false,
  spirometryQuality: false,
});

const USER_WEB_APP_ELEMENT_FEATURE_DEFAULTS = Object.freeze({
  poincareChart: false,
  respiratorySignalChart: false,
  spirometrySegmentChart: false,
});

const USER_WEB_APP_MISC_FEATURE_DEFAULTS = Object.freeze({
  weatherDetails: false,
});

const QUALITY_STATUS_PILL_PROFILE_IDS = new Set([
  "us-cds",
  "us-510k-denovo",
]);

const STUDY_SCOPED_RESULT_FEATURE_KEYS = new Set(
  Object.keys(USER_WEB_APP_RESULT_FEATURE_DEFAULTS).filter(
    (featureKey) => featureKey !== "resultsTrends",
  ),
);

function normalizeProfileId(profileId) {
  if (typeof profileId !== "string") {
    return null;
  }

  const trimmed = profileId.trim().toLowerCase();
  return trimmed || null;
}

function shouldPreferFallbackProfile(productPresentationProfile, fallbackProfileId) {
  const normalizedFallbackId = normalizeProfileId(fallbackProfileId);

  if (!normalizedFallbackId) {
    return false;
  }

  const normalizedLiveProfileId = normalizeProfileId(productPresentationProfile?.id);
  return normalizedLiveProfileId !== normalizedFallbackId;
}

function resolveFeatureMap(
  source,
  defaults,
  upperBound = null,
  { scopedKeys = null } = {},
) {
  return Object.keys(defaults).reduce((resolved, key) => {
    const defaultValue =
      typeof source?.[key] === "boolean" ? source[key] : defaults[key];

    if (scopedKeys instanceof Set && !scopedKeys.has(key)) {
      resolved[key] = Boolean(defaultValue);
      return resolved;
    }

    const scopedValue =
      typeof upperBound?.[key] === "boolean" ? upperBound[key] : defaultValue;

    resolved[key] = Boolean(defaultValue) && Boolean(scopedValue);
    return resolved;
  }, {});
}

export function resolveUserWebAppResultFeatures(
  productPresentationProfile,
  userWebAppFeatureAccess = null,
) {
  return resolveFeatureMap(
    productPresentationProfile?.userWebApp?.results,
    USER_WEB_APP_RESULT_FEATURE_DEFAULTS,
    userWebAppFeatureAccess?.results,
    { scopedKeys: STUDY_SCOPED_RESULT_FEATURE_KEYS },
  );
}

export function resolveUserWebAppElementFeatures(
  productPresentationProfile,
  userWebAppFeatureAccess = null,
) {
  return resolveFeatureMap(
    productPresentationProfile?.userWebApp?.elements,
    USER_WEB_APP_ELEMENT_FEATURE_DEFAULTS,
    userWebAppFeatureAccess?.elements,
  );
}

export function resolveUserWebAppMiscFeatures(
  productPresentationProfile,
  userWebAppFeatureAccess = null,
) {
  return resolveFeatureMap(
    productPresentationProfile?.userWebApp?.misc,
    USER_WEB_APP_MISC_FEATURE_DEFAULTS,
    userWebAppFeatureAccess?.misc,
  );
}

export function resolveUserWebAppQualityStatusPills(
  productPresentationProfile,
  fallbackProfileId = null,
) {
  const effectiveLiveProfile = shouldPreferFallbackProfile(
    productPresentationProfile,
    fallbackProfileId,
  )
    ? null
    : productPresentationProfile;

  const explicitLiveValue =
    effectiveLiveProfile?.userWebApp?.misc?.qualityStatusPills;
  if (typeof explicitLiveValue === "boolean") {
    return explicitLiveValue;
  }

  const normalizedLiveProfileId = normalizeProfileId(effectiveLiveProfile?.id);
  if (QUALITY_STATUS_PILL_PROFILE_IDS.has(normalizedLiveProfileId)) {
    return true;
  }

  return QUALITY_STATUS_PILL_PROFILE_IDS.has(
    normalizeProfileId(fallbackProfileId),
  );
}
